import React, { useEffect, useMemo, useState } from 'react';

import { FilterOutlined } from '@ant-design/icons';
import {
  Alert,
  App,
  Badge,
  Button,
  Col,
  Divider,
  Form,
  InputNumber,
  Modal,
  Popover,
  Row,
  Select,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import Search from 'antd/es/input/Search';
import Column from 'antd/es/table/Column';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { ApiErrorData } from 'models/ApiResponse';
import { AdminCityRow, AdminUserDetail, ProfileStatusEntry } from 'models/Lead';
import {
  AvailableLead,
  AvailableLeadsParams,
  AvailableLeadsResponse,
} from 'models/Requirement';

import { LeadService } from 'services/LeadService';
import { RequirementService } from 'services/RequirementService';

import appI18n from '../../../../config/i18n';
import DetailLeadModal from '../../../Leads/Components/DetailLeadModal';

const APTITUDE_OPTIONS: { value: 'A' | 'B' | 'C'; label: string }[] = [
  { value: 'A', label: 'A' },
  { value: 'B', label: 'B' },
  { value: 'C', label: 'C' },
];

const PROFILE_OPTIONS = [1, 2, 3, 4, 5].map((n) => ({
  value: n,
  label: String(n),
}));

const APTITUDE_COLOR: Record<'A' | 'B' | 'C', string> = {
  A: 'green',
  B: 'gold',
  C: 'red',
};

const AVATAR_GRADIENTS = [
  'linear-gradient(135deg, #1677FF, #4096FF)',
  'linear-gradient(135deg, #722ED1, #B37FEB)',
  'linear-gradient(135deg, #13C2C2, #36CFC9)',
  'linear-gradient(135deg, #FA8C16, #FFA940)',
  'linear-gradient(135deg, #52C41A, #95DE64)',
  'linear-gradient(135deg, #EB2F96, #F759AB)',
];

const getInitials = (firstName: string, lastName: string): string => {
  const a = (firstName || '').trim().charAt(0);
  const b = (lastName || '').trim().charAt(0);
  return `${a}${b}`.toUpperCase() || '?';
};

const getAvatarGradient = (key: string): string => {
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  }
  return AVATAR_GRADIENTS[hash % AVATAR_GRADIENTS.length];
};

const computeAge = (birthday: string | null): number | null => {
  if (!birthday) return null;
  const d = dayjs(birthday);
  if (!d.isValid()) return null;
  return dayjs().diff(d, 'year');
};

interface Props {
  isOpen: boolean;
  requirementId: string;
  countryCode: string;
  leadStatuses: ProfileStatusEntry[];
  handleCancel: () => void;
  onAssociated: () => void;
}

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

type SortField = 'fullName' | 'scoreProfile' | 'scoreAptitude';

// Mapea la columna de la tabla al campo de orden que acepta el endpoint.
const SORT_BY_KEY: Record<string, SortField> = {
  lead: 'fullName',
  aptitude: 'scoreAptitude',
  profile: 'scoreProfile',
};

function AssociateLeadsModal({
  isOpen,
  requirementId,
  countryCode,
  leadStatuses,
  handleCancel,
  onAssociated,
}: Props) {
  const { t } = useTranslation(undefined, { i18n: appI18n });
  const { message } = App.useApp();

  const [loading, setLoading] = useState<boolean>(false);
  const [rows, setRows] = useState<AvailableLead[]>([]);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [sort, setSort] = useState<SortField>('fullName');
  const [dir, setDir] = useState<'asc' | 'desc'>('asc');

  const [submitting, setSubmitting] = useState<boolean>(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  // Modal de detalle del lead
  const [leadModalOpen, setLeadModalOpen] = useState<boolean>(false);
  const [leadModalUserId, setLeadModalUserId] = useState<string | null>(null);

  // Ciudades
  const [cities, setCities] = useState<AdminCityRow[]>([]);

  // Filtros aplicados (disparan el fetch)
  const [term, setTerm] = useState<string>('');
  const [cityIds, setCityIds] = useState<number[]>([]);
  const [scoreAptitude, setScoreAptitude] = useState<('A' | 'B' | 'C')[]>([]);
  const [scoreProfile, setScoreProfile] = useState<number[]>([]);
  const [minAge, setMinAge] = useState<number | null>(null);
  const [maxAge, setMaxAge] = useState<number | null>(null);

  // Filtros en borrador (los que se editan dentro del panel)
  const [draftCityIds, setDraftCityIds] = useState<number[]>([]);
  const [draftAptitude, setDraftAptitude] = useState<('A' | 'B' | 'C')[]>([]);
  const [draftProfile, setDraftProfile] = useState<number[]>([]);
  const [draftMinAge, setDraftMinAge] = useState<number | null>(null);
  const [draftMaxAge, setDraftMaxAge] = useState<number | null>(null);
  const [filtersOpen, setFiltersOpen] = useState<boolean>(false);

  // Label de ciudad variable según país (Comuna/Distrito/Municipio/Ciudad).
  const cityLabel = useMemo(() => {
    const c = (countryCode || '').toUpperCase();
    const key = c === 'CL' || c === 'PE' || c === 'CO' ? c : 'default';
    return t(
      `scenes.requirements.detail.associate_modal.filters.city_labels.${key}`
    );
  }, [countryCode, t]);

  const resetState = () => {
    setRows([]);
    setSelectedRowKeys([]);
    setPage(1);
    setTotal(0);
    setTerm('');
    setCityIds([]);
    setScoreAptitude([]);
    setScoreProfile([]);
    setMinAge(null);
    setMaxAge(null);
    setDraftCityIds([]);
    setDraftAptitude([]);
    setDraftProfile([]);
    setDraftMinAge(null);
    setDraftMaxAge(null);
    setFiltersOpen(false);
  };

  useEffect(() => {
    if (isOpen) {
      resetState();
      fetchCities();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    fetchLeads();
  }, [
    isOpen,
    page,
    pageSize,
    sort,
    dir,
    term,
    cityIds,
    scoreAptitude,
    scoreProfile,
    minAge,
    maxAge,
  ]);

  // Reset de página al cambiar filtros aplicados
  useEffect(() => {
    setPage(1);
  }, [term, cityIds, scoreAptitude, scoreProfile, minAge, maxAge]);

  const fetchCities = async () => {
    const response = await LeadService.getCities(countryCode);
    if (response.success) {
      setCities(response.data as AdminCityRow[]);
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) ||
          t('scenes.requirements.detail.associate_modal.errors.fetch_cities'),
        3.5
      );
    }
  };

  const fetchLeads = async () => {
    setLoading(true);
    const params: AvailableLeadsParams = {
      page,
      pageSize,
      sort,
      dir,
    };
    if (term) params.term = term;
    if (cityIds.length > 0) params.cityIds = cityIds;
    if (scoreAptitude.length > 0) params.scoreAptitude = scoreAptitude;
    if (scoreProfile.length > 0) params.scoreProfile = scoreProfile;
    if (minAge !== null) params.minAge = minAge;
    if (maxAge !== null) params.maxAge = maxAge;

    const response = await RequirementService.getAvailableLeads(
      requirementId,
      params
    );

    if (response.success) {
      const data = response.data as AvailableLeadsResponse;
      setRows(data.data || []);
      setTotal(data.totalRows || 0);
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) ||
          t('scenes.requirements.detail.associate_modal.errors.fetch_leads'),
        3.5
      );
    }
    setLoading(false);
  };

  // Texto completo "Ciudad - Región" por id, usado como title en hover.
  const cityFullById = useMemo(() => {
    const map: Record<number, string> = {};
    cities.forEach((c) => {
      map[c.id] = c.regionName ? `${c.cityName} - ${c.regionName}` : c.cityName;
    });
    return map;
  }, [cities]);

  const cityOptions = useMemo(
    () =>
      cities.map((c) => ({
        value: c.id,
        label: c.cityName,
        title: cityFullById[c.id],
      })),
    [cities, cityFullById]
  );

  const activeFiltersCount =
    (cityIds.length > 0 ? 1 : 0) +
    (scoreAptitude.length > 0 ? 1 : 0) +
    (scoreProfile.length > 0 ? 1 : 0) +
    (minAge !== null || maxAge !== null ? 1 : 0);

  const handleFiltersOpenChange = (open: boolean) => {
    if (open) {
      setDraftCityIds(cityIds);
      setDraftAptitude(scoreAptitude);
      setDraftProfile(scoreProfile);
      setDraftMinAge(minAge);
      setDraftMaxAge(maxAge);
    }
    setFiltersOpen(open);
  };

  const handleApplyFilters = () => {
    const ages = [draftMinAge, draftMaxAge].filter(
      (a): a is number => a !== null
    );
    if (ages.some((a) => a < 18 || a > 130)) {
      message.warning(
        t(
          'scenes.requirements.detail.associate_modal.filters.age_range_invalid'
        ),
        3.5
      );
      return;
    }
    if (
      draftMinAge !== null &&
      draftMaxAge !== null &&
      draftMaxAge < draftMinAge
    ) {
      message.warning(
        t('scenes.requirements.detail.associate_modal.filters.age_invalid'),
        3.5
      );
      return;
    }
    setCityIds(draftCityIds);
    setScoreAptitude(draftAptitude);
    setScoreProfile(draftProfile);
    setMinAge(draftMinAge);
    setMaxAge(draftMaxAge);
    setFiltersOpen(false);
  };

  const handleClearFilters = () => {
    setDraftCityIds([]);
    setDraftAptitude([]);
    setDraftProfile([]);
    setDraftMinAge(null);
    setDraftMaxAge(null);
  };

  const handleAssociate = async () => {
    const userIds = selectedRowKeys.map((k) => String(k));
    if (userIds.length === 0) return;

    setSubmitting(true);
    const response = await RequirementService.associateLeads(
      requirementId,
      userIds
    );
    setSubmitting(false);

    if (response.success) {
      message.success(
        t('scenes.requirements.detail.associate_modal.success', {
          count: userIds.length,
        }),
        3
      );
      onAssociated();
      handleCancel();
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) ||
          t(
            'scenes.requirements.detail.associate_modal.errors.associate_failed'
          ),
        3.5
      );
      const offending =
        (error as any)?.offendingIds || (error as any)?.conflictIds;
      if (Array.isArray(offending) && offending.length > 0) {
        message.info(
          t('scenes.requirements.detail.associate_modal.errors.offending', {
            count: offending.length,
          }),
          3.5
        );
      }
    }
  };

  const openLeadModal = (userId: string) => {
    setLeadModalUserId(userId);
    setLeadModalOpen(true);
  };

  const closeLeadModal = () => {
    setLeadModalOpen(false);
    setLeadModalUserId(null);
  };

  // Tras editar el lead en el modal de detalle, actualiza su fila in-place.
  // Si dejó de ser active_worker, lo quita de la lista de disponibles; si
  // volvió a serlo (y aún no se cerró el modal), lo vuelve a añadir.
  const handleLeadUpdated = async () => {
    if (!leadModalUserId) return;
    const response = await LeadService.getById(leadModalUserId);
    if (!response.success) return;
    const { profile, city, user } = response.data as AdminUserDetail;
    const isActiveWorker = profile.statusCode === 'active_worker';
    const mapped: AvailableLead = {
      userId: leadModalUserId,
      email: user.email,
      firstName: profile.firstName,
      lastName: profile.lastName,
      avatarUrl: profile.avatarUrl,
      rut: profile.rut,
      birthday: profile.birthday,
      cityId: profile.cityId,
      cityName: city?.cityName ?? null,
      provinceName: city?.provinceName ?? null,
      regionName: city?.regionName ?? null,
      provinceId: city?.provinceId ?? null,
      regionId: city?.regionId ?? null,
      scoreProfile: profile.scoreProfile,
      scoreAptitude: profile.scoreAptitude,
    };
    setRows((prev) => {
      if (!isActiveWorker) {
        return prev.filter((r) => r.userId !== leadModalUserId);
      }
      if (prev.some((r) => r.userId === leadModalUserId)) {
        return prev.map((r) => (r.userId === leadModalUserId ? mapped : r));
      }
      return [mapped, ...prev];
    });
  };

  const renderAvatar = (row: AvailableLead) => (
    <span
      className="requirement-detail-avatar"
      style={{ background: getAvatarGradient(row.userId) }}
    >
      {row.avatarUrl ? (
        <img src={row.avatarUrl} alt="" />
      ) : (
        getInitials(row.firstName, row.lastName)
      )}
    </span>
  );

  const filtersContent = (
    <div style={{ width: 340 }}>
      <Form layout="vertical" style={{ marginBottom: 0 }}>
        <Form.Item label={cityLabel} style={{ marginBottom: 12 }}>
          <Select
            mode="multiple"
            value={draftCityIds}
            onChange={(value) => setDraftCityIds(value as number[])}
            options={cityOptions}
            showSearch
            optionFilterProp="label"
            allowClear
            maxTagCount="responsive"
            tagRender={(tagProps) => (
              <Tag
                title={
                  cityFullById[tagProps.value as number] ||
                  String(tagProps.label)
                }
                closable={tagProps.closable}
                onClose={tagProps.onClose}
                style={{ marginInlineEnd: 4 }}
              >
                {tagProps.label}
              </Tag>
            )}
            placeholder={t(
              'scenes.requirements.detail.associate_modal.filters.city_placeholder'
            )}
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item
          label={t(
            'scenes.requirements.detail.associate_modal.filters.aptitude_label'
          )}
          style={{ marginBottom: 12 }}
        >
          <Select
            mode="multiple"
            value={draftAptitude}
            onChange={(value) => setDraftAptitude(value as ('A' | 'B' | 'C')[])}
            options={APTITUDE_OPTIONS}
            allowClear
            maxTagCount="responsive"
            placeholder={t(
              'scenes.requirements.detail.associate_modal.filters.aptitude_placeholder'
            )}
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item
          label={t(
            'scenes.requirements.detail.associate_modal.filters.profile_label'
          )}
          style={{ marginBottom: 12 }}
        >
          <Select
            mode="multiple"
            value={draftProfile}
            onChange={(value) => setDraftProfile(value as number[])}
            options={PROFILE_OPTIONS}
            allowClear
            maxTagCount="responsive"
            placeholder={t(
              'scenes.requirements.detail.associate_modal.filters.profile_placeholder'
            )}
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item
          label={t(
            'scenes.requirements.detail.associate_modal.filters.age_label'
          )}
          style={{ marginBottom: 0 }}
        >
          <Row gutter={8}>
            <Col span={12}>
              <InputNumber
                min={18}
                max={130}
                value={draftMinAge ?? undefined}
                onChange={(v) => setDraftMinAge(v as number | null)}
                placeholder={t(
                  'scenes.requirements.detail.associate_modal.filters.min_age_placeholder'
                )}
                style={{ width: '100%' }}
              />
            </Col>
            <Col span={12}>
              <InputNumber
                min={18}
                max={130}
                value={draftMaxAge ?? undefined}
                onChange={(v) => setDraftMaxAge(v as number | null)}
                placeholder={t(
                  'scenes.requirements.detail.associate_modal.filters.max_age_placeholder'
                )}
                style={{ width: '100%' }}
              />
            </Col>
          </Row>
        </Form.Item>
      </Form>
      <Divider style={{ margin: '16px 0 12px' }} />
      <Row justify="space-between" align="middle">
        <Col>
          <Button type="text" onClick={handleClearFilters}>
            {t('scenes.requirements.detail.associate_modal.filters.clear')}
          </Button>
        </Col>
        <Col>
          <Button type="primary" onClick={handleApplyFilters}>
            {t('scenes.requirements.detail.associate_modal.filters.apply')}
          </Button>
        </Col>
      </Row>
    </div>
  );

  return (
    <>
      <Modal
        title={t('scenes.requirements.detail.associate_modal.title')}
        open={isOpen}
        onCancel={handleCancel}
        onOk={handleAssociate}
        okText={t(
          'scenes.requirements.detail.associate_modal.associate_button',
          {
            count: selectedRowKeys.length,
          }
        )}
        cancelText={t('scenes.requirements.detail.associate_modal.cancel')}
        okButtonProps={{
          disabled: selectedRowKeys.length === 0,
          loading: submitting,
        }}
        destroyOnClose
        width={900}
        maskClosable={false}
      >
        <Divider style={{ marginTop: '15px', marginBottom: '15px' }} />
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 14 }}
          message={t('scenes.requirements.detail.associate_modal.banner')}
        />

        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 12 }}
        >
          <Col flex="none">
            <Search
              placeholder={t(
                'scenes.requirements.detail.associate_modal.filters.search_placeholder'
              )}
              onSearch={(value) => setTerm(value)}
              allowClear
              style={{ width: 280 }}
            />
          </Col>
          <Col flex="none">
            <Popover
              trigger="click"
              placement="bottomRight"
              open={filtersOpen}
              onOpenChange={handleFiltersOpenChange}
              content={filtersContent}
              destroyTooltipOnHide
            >
              <Badge count={activeFiltersCount} size="small" offset={[-4, 4]}>
                <Button icon={<FilterOutlined />}>
                  {t(
                    'scenes.requirements.detail.associate_modal.filters.advanced'
                  )}
                </Button>
              </Badge>
            </Popover>
          </Col>
        </Row>

        <Table<AvailableLead>
          dataSource={rows}
          bordered
          loading={loading}
          size="small"
          scroll={{ x: 720 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: PAGE_SIZE_OPTIONS.map(String),
          }}
          rowKey={(record) => record.userId}
          onChange={(pagination, _filters, sorter) => {
            if (pagination.current && pagination.current !== page) {
              setPage(pagination.current);
            }
            if (pagination.pageSize && pagination.pageSize !== pageSize) {
              setPageSize(pagination.pageSize);
              setPage(1);
            }
            const s = sorter as { columnKey?: string; order?: string };
            if (s?.order && s.columnKey && SORT_BY_KEY[s.columnKey]) {
              setSort(SORT_BY_KEY[s.columnKey]);
              setDir(s.order === 'descend' ? 'desc' : 'asc');
            } else {
              setSort('fullName');
              setDir('asc');
            }
          }}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
            preserveSelectedRowKeys: true,
          }}
        >
          <Column
            title={t('scenes.requirements.detail.associate_modal.columns.lead')}
            key="lead"
            sorter
            sortOrder={
              sort === 'fullName'
                ? dir === 'asc'
                  ? 'ascend'
                  : 'descend'
                : null
            }
            render={(row: AvailableLead) => (
              <div className="requirement-detail-lead-cell">
                {renderAvatar(row)}
                <div className="requirement-detail-lead-info">
                  <button
                    type="button"
                    className="requirement-detail-lead-link"
                    onClick={() => openLeadModal(row.userId)}
                  >
                    {row.firstName} {row.lastName}
                  </button>
                  {row.rut && (
                    <span className="requirement-detail-lead-doc">
                      {row.rut}
                    </span>
                  )}
                </div>
              </div>
            )}
          />
          <Column
            title={cityLabel}
            key="city"
            render={(row: AvailableLead) => {
              const full = [row.cityName, row.provinceName, row.regionName]
                .filter(Boolean)
                .join(' · ');
              return (
                <Tooltip title={full || undefined}>
                  <span>{row.cityName || '—'}</span>
                </Tooltip>
              );
            }}
          />
          <Column
            width={80}
            align="center"
            title={t('scenes.requirements.detail.associate_modal.columns.age')}
            key="age"
            render={(row: AvailableLead) => {
              const age = computeAge(row.birthday);
              return <span>{age !== null ? age : '—'}</span>;
            }}
          />
          <Column
            width={90}
            align="center"
            title={t(
              'scenes.requirements.detail.associate_modal.columns.aptitude'
            )}
            key="aptitude"
            sorter
            sortOrder={
              sort === 'scoreAptitude'
                ? dir === 'asc'
                  ? 'ascend'
                  : 'descend'
                : null
            }
            render={(row: AvailableLead) =>
              row.scoreAptitude ? (
                <Tag
                  color={APTITUDE_COLOR[row.scoreAptitude]}
                  style={{ marginRight: 0 }}
                >
                  {row.scoreAptitude}
                </Tag>
              ) : (
                <span>—</span>
              )
            }
          />
          <Column
            width={90}
            align="center"
            title={t(
              'scenes.requirements.detail.associate_modal.columns.profile'
            )}
            key="profile"
            sorter
            sortOrder={
              sort === 'scoreProfile'
                ? dir === 'asc'
                  ? 'ascend'
                  : 'descend'
                : null
            }
            render={(row: AvailableLead) => (
              <span>
                {row.scoreProfile !== null ? `${row.scoreProfile} ★` : '—'}
              </span>
            )}
          />
        </Table>
      </Modal>
      {leadModalUserId && (
        <DetailLeadModal
          isOpen={leadModalOpen}
          leadId={leadModalUserId}
          handleModalCancel={closeLeadModal}
          onAfterUpdate={handleLeadUpdated}
          statuses={leadStatuses}
        />
      )}
    </>
  );
}

export default AssociateLeadsModal;
